import {
  adminDownloadEvidence,
  adminRevealPersonnummer,
  adminUpdateRemovalRequest,
  adminUploadEvidence,
} from "@/api/api";
import { adminCaseQuery } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  caseStatusConfig,
  removalStatusConfig,
  removalStatusOrder,
  siteMethodLabel,
} from "@/lib/adminLabels";
import {
  evidenceUploadSchema,
  type EvidenceUploadInput,
} from "@/schemas/evidence";
import type { AdminCaseDetail, AdminRemovalRequest } from "@/types/admin";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  Eye,
  FileSignature,
  Loader2,
  ShieldAlert,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/cases/$caseId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(adminCaseQuery(params.caseId)),
  component: AdminCaseDetailPage,
});

function useCaseInvalidate(caseId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "case", caseId] });
}

function AdminCaseDetailPage() {
  const { caseId } = Route.useParams();
  const { data: detail, isError, isLoading } = useQuery(adminCaseQuery(caseId));

  if (isError) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        Ärendet kunde inte hittas.{" "}
        <Link to="/admin" className="text-cyan-600 hover:underline">
          Tillbaka
        </Link>
      </div>
    );
  }

  if (isLoading || !detail) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const caseStatus = caseStatusConfig[detail.status];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/admin"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Ärenden
        </Link>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            {detail.customer.name ?? detail.customer.email}
          </h2>
          <p className="text-sm text-muted-foreground">
            {detail.customer.email}
          </p>
        </div>
        <Badge className={caseStatus.className}>{caseStatus.label}</Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <CustomerCard detail={detail} />
        <FullmaktCard detail={detail} />
      </div>

      <RemovalRequestsCard detail={detail} caseId={caseId} />

      <EvidenceCard detail={detail} caseId={caseId} />
    </div>
  );
}

function CustomerCard({ detail }: { detail: AdminCaseDetail }) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const { customer } = detail;

  const reveal = useMutation({
    mutationFn: () => adminRevealPersonnummer(detail.id),
    onSuccess: ({ personnummer }) => {
      setRevealed(personnummer);
      toast.info("Personnummer visat – visningen har loggats.");
    },
    onError: () => toast.error("Kunde inte visa personnummer."),
  });

  return (
    <Card>
      <CardHeader className="font-medium">Kund</CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Field label="Namn" value={customer.name ?? "—"} />
        <Field label="E-post" value={customer.email} />
        <div>
          <div className="text-xs text-muted-foreground">Personnummer</div>
          {!customer.hasPersonnummer ? (
            <div className="mt-1 flex items-center gap-2 text-muted-foreground">
              <ShieldAlert className="size-4" />
              Raderat efter slutförd radering
            </div>
          ) : revealed ? (
            <div className="mt-1 font-mono">{revealed}</div>
          ) : (
            <div className="mt-1 flex items-center gap-3">
              <span className="font-mono">{customer.personnummerMasked}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => reveal.mutate()}
                disabled={reveal.isPending}
              >
                {reveal.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Eye className="size-3.5" />
                )}
                Visa
              </Button>
            </div>
          )}
        </div>
        {detail.monitoringUntil && (
          <Field
            label="Övervakning t.o.m."
            value={new Date(detail.monitoringUntil).toLocaleDateString("sv-SE")}
          />
        )}
        {detail.piiPurgedAt && (
          <Field
            label="PII raderat"
            value={new Date(detail.piiPurgedAt).toLocaleDateString("sv-SE")}
          />
        )}
      </CardContent>
    </Card>
  );
}

function FullmaktCard({ detail }: { detail: AdminCaseDetail }) {
  const fullmakt = detail.fullmakter[0];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between font-medium">
        <span>Fullmakt</span>
        {fullmakt &&
          (fullmakt.signed ? (
            <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-800">
              <FileSignature className="size-3" />
              Signerad
            </Badge>
          ) : (
            <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800">
              Ej signerad
            </Badge>
          ))}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!fullmakt ? (
          <p className="text-muted-foreground">Ingen fullmakt registrerad.</p>
        ) : (
          <>
            <p className="rounded-md border bg-muted/40 p-3 text-muted-foreground">
              {fullmakt.signedText}
            </p>
            <Field
              label="Signerad"
              value={new Date(fullmakt.signedAt).toLocaleString("sv-SE")}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RemovalRequestsCard({
  detail,
  caseId,
}: {
  detail: AdminCaseDetail;
  caseId: string;
}) {
  return (
    <Card>
      <CardHeader className="font-medium">Raderingsbegäranden</CardHeader>
      <CardContent className="space-y-3">
        {detail.removalRequests.map((req) => (
          <RemovalRequestRow key={req.id} req={req} caseId={caseId} />
        ))}
        {detail.removalRequests.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Inga begäranden ännu (skapas när betalning genomförts).
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RemovalRequestRow({
  req,
  caseId,
}: {
  req: AdminRemovalRequest;
  caseId: string;
}) {
  const [notes, setNotes] = useState(req.notes ?? "");
  const invalidate = useCaseInvalidate(caseId);

  const update = useMutation({
    mutationFn: (body: { status?: AdminRemovalRequest["status"]; notes?: string | null }) =>
      adminUpdateRemovalRequest(req.id, body),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Kunde inte uppdatera begäran."),
  });

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{req.site.name}</span>
          <Badge variant="outline" className="text-xs">
            {siteMethodLabel[req.site.method]}
          </Badge>
          <Badge className={removalStatusConfig[req.status].className}>
            {removalStatusConfig[req.status].label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {update.isPending && (
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          )}
          <select
            value={req.status}
            onChange={(e) =>
              update.mutate({
                status: e.target.value as AdminRemovalRequest["status"],
              })
            }
            disabled={update.isPending}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            {removalStatusOrder.map((s) => (
              <option key={s} value={s}>
                {removalStatusConfig[s].label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            if (notes !== (req.notes ?? "")) update.mutate({ notes: notes || null });
          }}
          placeholder="Anteckning…"
          className="h-8 text-sm"
        />
      </div>
    </div>
  );
}

const EVIDENCE_TYPES: { value: EvidenceUploadInput["type"]; label: string }[] = [
  { value: "screenshot", label: "Skärmdump" },
  { value: "email", label: "E-post" },
  { value: "letter", label: "Brev" },
  { value: "report", label: "Rapport" },
  { value: "fullmakt", label: "Fullmakt" },
];

function EvidenceCard({
  detail,
  caseId,
}: {
  detail: AdminCaseDetail;
  caseId: string;
}) {
  const [file, setFile] = useState<File | null>(null);
  const invalidate = useCaseInvalidate(caseId);
  const { register, handleSubmit, reset } = useForm<EvidenceUploadInput>({
    resolver: zodResolver(evidenceUploadSchema),
    defaultValues: { type: "screenshot", removalRequestId: "" },
  });

  const upload = useMutation({
    mutationFn: (data: EvidenceUploadInput) =>
      adminUploadEvidence(
        caseId,
        file!,
        data.type,
        data.removalRequestId || undefined,
      ),
    onSuccess: () => {
      toast.success("Bevis uppladdat.");
      setFile(null);
      reset({ type: "screenshot", removalRequestId: "" });
      invalidate();
    },
    onError: () => toast.error("Kunde inte ladda upp bevis."),
  });

  const onSubmit = (data: EvidenceUploadInput) => {
    if (!file) {
      toast.error("Välj en fil först.");
      return;
    }
    upload.mutate(data);
  };

  const requestLabel = (id: string | null) => {
    if (!id) return null;
    const req = detail.removalRequests.find((r) => r.id === id);
    return req ? req.site.name : null;
  };

  return (
    <Card>
      <CardHeader className="font-medium">Bevis</CardHeader>
      <CardContent className="space-y-4">
        {detail.evidence.length > 0 && (
          <div className="divide-y rounded-md border">
            {detail.evidence.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-2 p-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{e.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {EVIDENCE_TYPES.find((t) => t.value === e.type)?.label ??
                      e.type}
                    {requestLabel(e.removalRequestId) &&
                      ` · ${requestLabel(e.removalRequestId)}`}
                    {" · "}
                    {new Date(e.createdAt).toLocaleDateString("sv-SE")}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => adminDownloadEvidence(e.id, e.filename)}
                >
                  <Download className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-2"
        >
          <div className="space-y-1">
            <Label className="text-xs">Fil</Label>
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Typ</Label>
            <select
              {...register("type")}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              {EVIDENCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Koppla till begäran (valfritt)</Label>
            <select
              {...register("removalRequestId")}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">— Ingen —</option>
              {detail.removalRequests.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={upload.isPending} className="w-full">
              {upload.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              Ladda upp
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
